import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { ApiError } from '../api/client';
import { ownerApi } from '../api/owner';
import { QuotaBanner } from '../components/QuotaBanner';
import { SuspendedBanner } from '../components/SuspendedBanner';
import type { CustomHtmlData, OwnerBusiness, QuotaStatus } from '../types';

export function CustomHtmlPage() {
  const [business, setBusiness] = useState<OwnerBusiness | null>(null);
  const [quota, setQuota] = useState<QuotaStatus | null>(null);
  const [data, setData] = useState<CustomHtmlData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [b, q] = await Promise.all([ownerApi.getBusiness(), ownerApi.getQuota()]);
        setBusiness(b);
        setQuota(q);
        const html = await ownerApi.getCustomHtml();
        setData(html);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not load your page content.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleChange = (variable: string, value: string) => {
    setData((prev) => (prev ? { ...prev, values: { ...prev.values, [variable]: value } } : prev));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!data) return;
    setSaving(true);
    setMessage(null);
    try {
      await ownerApi.updateCustomHtml(data.values);
      const q = await ownerApi.getQuota();
      setQuota(q);
      setMessage({ type: 'success', text: 'Page content updated.' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setMessage({ type: 'error', text: 'Monthly edit limit reached. Try again next month.' });
      } else if (err instanceof ApiError && err.status === 403) {
        setMessage({ type: 'error', text: 'Business is suspended — cannot save changes.' });
      } else {
        setMessage({ type: 'error', text: 'Could not save changes.' });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-slate-500">Loading…</p>;

  if (error || !data) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-semibold text-slate-900">HTML Page</h1>
        <p className="text-sm text-red-600">
          {error ?? 'No custom HTML template is assigned to your business yet. Contact support.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-slate-900">HTML Page</h1>
      <p className="text-sm text-slate-500">
        Your admin has designed this page's layout. You can only fill in the values
        below — the layout and variable names are fixed.
      </p>

      {business?.is_suspended && <SuspendedBanner />}
      {quota && <QuotaBanner quota={quota} />}

      {data.variables.length === 0 ? (
        <p className="text-sm text-slate-500">This template has no editable values.</p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4 rounded-brand border border-slate-200 bg-white p-6">
          {data.variables.map((variable) => (
            <div key={variable}>
              <label className="block text-sm font-medium text-slate-700">{`{${variable}}`}</label>
              <input
                type="text"
                value={data.values[variable] ?? ''}
                onChange={(e) => handleChange(variable, e.target.value)}
                disabled={business?.is_suspended}
                className="mt-1 w-full rounded-brand border border-slate-300 px-3 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:bg-slate-100"
              />
            </div>
          ))}

          {message && (
            <p className={`text-sm ${message.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>
              {message.text}
            </p>
          )}

          <button
            type="submit"
            disabled={saving || business?.is_suspended || (quota ? quota.remaining <= 0 : false)}
            className="rounded-brand bg-brand-primary px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </form>
      )}
    </div>
  );
}
